import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const CLAUSES = [
  {
    n: "01",
    title: "Acceptance of Terms",
    body:
      "By accessing or using theblackhouse.in, you agree to these Terms & Conditions. If you do not agree, please discontinue use of the website.",
  },
  {
    n: "02",
    title: "Eligibility",
    body:
      "You must be legally capable of entering into a binding contract under applicable law. If you are under 18 years of age, purchases must be made with the involvement of a parent or legal guardian.",
  },
  {
    n: "03",
    title: "Products",
    body:
      "The Black House offers limited-edition gender-neutral apparel. Product colours, textures and measurements may vary slightly due to manufacturing and display differences.",
  },
  {
    n: "04",
    title: "Pricing & Orders",
    body:
      "All prices are listed with the Indian Rupee symbol (₹) unless stated otherwise. We reserve the right to update prices, decline or cancel orders in cases of pricing errors, suspected fraud, stock limitations or technical issues. An order confirmation does not constitute final acceptance until the order is dispatched.",
  },
  {
    n: "05",
    title: "Shipping",
    body:
      "Estimated delivery timelines are indicative and may vary due to logistics, weather, holidays or other circumstances beyond our control.",
  },
  {
    n: "06",
    title: "Returns & Exchanges",
    body:
      "Returns, exchanges and refunds are governed by our separate Return Policy published on the website.",
  },
  {
    n: "07",
    title: "Intellectual Property",
    body:
      "All logos, designs, graphics, photographs, product artwork, branding, website content and other intellectual property are owned by or licensed to The Black House. No material may be copied, reproduced or commercially used without prior written permission.",
  },
  {
    n: "08",
    title: "User Conduct",
    body:
      "Users must not misuse the website, interfere with its operation, upload unlawful content or attempt unauthorized access.",
  },
  {
    n: "09",
    title: "Privacy",
    body: "Personal information is processed according to our Privacy Policy.",
  },
  {
    n: "10",
    title: "Limitation of Liability",
    body:
      "To the maximum extent permitted by law, The Black House shall not be liable for indirect, incidental or consequential damages arising from use of the website or products.",
  },
  {
    n: "11",
    title: "Governing Law",
    body:
      "These Terms are governed by the laws of India. Courts having jurisdiction over Faridabad, Haryana shall have exclusive jurisdiction unless otherwise required by law.",
  },
  {
    n: "12",
    title: "Changes",
    body:
      "We may update these Terms from time to time. Continued use of the website constitutes acceptance of revised Terms.",
  },
];

export default function Terms() {
  useEffect(() => {
    document.title = "Terms & Conditions — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="terms-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Volume I
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        MMXXVI
      </motion.div>

      {/* Header */}
      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Terms & Conditions
          </p>
          <h1
            data-testid="terms-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            The Rules Of The House
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-lg mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("A concise ledger of the terms that govern your access to theblackhouse.in. Written plainly, so nothing is hidden in the shadows.")}
          </p>
        </motion.div>
      </header>

      {/* Clauses */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {CLAUSES.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.03, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`terms-clause-${c.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  Ch. {c.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {c.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(c.body)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="terms-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link
              to="/privacy"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Privacy
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/accessibility"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Accessibility
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/security"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Security
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <a
              href="https://www.instagram.com/theblackhouse.tbh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Instagram
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/the-black-house/"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Linkedin
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
